import type { ValueOf } from "../settings/value-types";
import { defineRule, type Rule } from "./types";

/**
 * Every rule in Walliyo, in one list (ADR-0013 §2.5).
 *
 * To add a rule:
 *   1. add it here with defineRule. Its `ruleId` is <NAME>_RULE, e.g.
 *      ID_IMAGE_RETENTION_RULE. It is permanent: versions and the
 *      audit log keep it forever;
 *   2. if its value type is new, add it to VALUE_TYPES in
 *      ../settings/value-types.ts;
 *   3. read it on the server with `currentRuleValue(ruleId)`. A rule nobody
 *      reads does nothing.
 * `definitions.t.ts` checks that every `ruleId` is unique, well formed, and
 * that its default fits its value type.
 *
 * `defaultValue` is what applies until the first version takes effect. Staff
 * with EDIT_RULES_STAFF_FEATURE_ENABLEMENT add versions; `critical` rules need
 * the Owner.
 */
export const RULES = [
  // Retired by ADR-0019: one ceiling for the whole company became each
  // permission's limit, set per role (ISSUE_REFUND_STAFF_PERMISSION_LIMIT and
  // its neighbours). Nothing reads it; it stays because its versions and the
  // audit log refer to it.
  defineRule({
    ruleId: "REFUND_OWNER_APPROVAL_ABOVE_RULE",
    name: "Owner approval above",
    category: "payments",
    valueType: "GnfAmount",
    defaultValue: 1_000_000,
    sensitivity: "critical",
    requiresLegalBasis: false,
    status: "retired",
  }),
  // How long ID images are kept after a verification decision. Personal data:
  // a change must cite the law, contract or legal advice behind it.
  defineRule({
    ruleId: "ID_IMAGE_RETENTION_RULE",
    name: "ID image retention",
    category: "onboarding",
    valueType: "Days",
    defaultValue: 90,
    sensitivity: "critical",
    requiresLegalBasis: true,
  }),
  // Support's target for the first reply to a new case.
  defineRule({
    ruleId: "FIRST_REPLY_TARGET_RULE",
    name: "First reply target",
    category: "support",
    valueType: "Hours",
    defaultValue: 2,
    sensitivity: "high",
    requiresLegalBasis: false,
  }),
  // Staff must review an interim suspension (ADR-0013 §5) within this time.
  // A deadline for staff, never an expiry: the suspension lasts until a staff
  // decision, and past this it shows as late.
  defineRule({
    ruleId: "INTERIM_SUSPENSION_REVIEW_RULE",
    name: "Interim suspension review",
    category: "safety",
    valueType: "Hours",
    defaultValue: 72,
    sensitivity: "high",
    requiresLegalBasis: false,
  }),
  // A job nobody answered expires after this long (EXPIRE_JOB_SYSTEM_ACTION).
  defineRule({
    ruleId: "UNANSWERED_JOB_EXPIRY_RULE",
    name: "Unanswered job request expiry",
    category: "jobs",
    valueType: "Hours",
    defaultValue: 48,
    sensitivity: "high",
    requiresLegalBasis: false,
  }),
  // After a renewal isn't paid, a subscription is suspended for this long
  // before it ends (ADR-0018 §3): no access, but its price and promo are held.
  // Read by the billing scheduler when a suspension starts.
  defineRule({
    ruleId: "SUBSCRIPTION_GRACE_PERIOD_RULE",
    name: "Subscription grace period",
    category: "payments",
    valueType: "Days",
    defaultValue: 3,
    sensitivity: "high",
    requiresLegalBasis: false,
  }),
  // A console session ends after this long without a request. Read by
  // staff-auth.ts on every staff request.
  defineRule({
    ruleId: "STAFF_SESSION_INACTIVITY_RULE",
    name: "Sign-out after inactivity",
    category: "access",
    valueType: "Minutes",
    defaultValue: 30,
    sensitivity: "critical",
    requiresLegalBasis: false,
  }),
] satisfies readonly Rule[];

export type DefinedRule = (typeof RULES)[number];

export type DefinedRuleId = DefinedRule["ruleId"];

/** One rule's value, e.g. RuleValueOf<"FIRST_REPLY_TARGET_RULE"> is number. */
export type RuleValueOf<I extends DefinedRuleId> = ValueOf<Extract<DefinedRule, { ruleId: I }>["valueType"]>;
